import { FC, MouseEventHandler } from 'react';
import { useSelector } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classNames from 'classnames';
import CheckCircle from 'dibs-icons/exports/legacy/CircleCheck';
import ReturnArrow from 'dibs-icons/exports/legacy/ReturnArrow';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';

// components
import { PdpRespSidebarBlurbShipping } from './PdpRespSidebarBlurbShipping';
import { PdpRespSidebarBlurbReturnPolicy } from './PdpRespSidebarBlurbReturnPolicy';
import { useRenderBlurbs } from './hooks/useRenderBlurbs';
import { useExposedReturnsClientVariant } from '../hooks/useExposedReturnsClientVariant';
import { RelayVariables, Store } from '../../global/relayVariableTypes';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import PdpSharedSidebarBuyerIncentives from '../shared/PdpSharedSidebarBuyerIncentives/PdpSharedSidebarBuyerIncentives';
const PdpSharedSidebarItemReturns = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedSidebarItemReturns" */ '../shared/PdpSharedSidebarItemReturns/PdpSharedSidebarItemReturns'
        )
);

import { PdpRespSidebarBlurbs_viewer$data } from './__generated__/PdpRespSidebarBlurbs_viewer.graphql';
import { PdpRespSidebarBlurbs_item$data } from './__generated__/PdpRespSidebarBlurbs_item.graphql';
import { PdpRespSidebarBlurbs_user$data } from './__generated__/PdpRespSidebarBlurbs_user.graphql';
import { PdpRespSidebarBlurbs_displayInfo$data } from './__generated__/PdpRespSidebarBlurbs_displayInfo.graphql';
import { PdpRespSidebarBlurbs_sku$data } from './__generated__/PdpRespSidebarBlurbs_sku.graphql';

// styles
import styles from './PdpRespSidebarBlurbs.scss';

type Props = {
    viewer: PdpRespSidebarBlurbs_viewer$data;
    item: PdpRespSidebarBlurbs_item$data;
    user: PdpRespSidebarBlurbs_user$data | null | undefined;
    displayInfo: PdpRespSidebarBlurbs_displayInfo$data | null | undefined;
    sku: PdpRespSidebarBlurbs_sku$data | null | undefined;
    currency?: string;
    itemQuantity?: number;
    isClient: boolean;
    buyerIncentivesSidebarClick: MouseEventHandler;
};

export const PdpRespSidebarBlurbsComponent: FC<Props> = ({
    currency = 'USD',
    viewer,
    item,
    user,
    displayInfo = null,
    sku,
    itemQuantity,
    isClient,
    buyerIncentivesSidebarClick,
}) => {
    const { isAuctionEnabled } = item || {};
    const { isRespPdpSiderailV1Variant } = useServerVarsContext();
    const reduceSpacing = isRespPdpSiderailV1Variant && !isAuctionEnabled;
    const renderBlurbs = useRenderBlurbs({ item, displayInfo });
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );
    const isExposedReturnsClientVariant = useExposedReturnsClientVariant({
        viewer: isClient ? viewer : null,
    });

    if (!renderBlurbs) {
        return null;
    }

    return (
        <div
            className={classNames(styles.container, {
                [styles.reduceSpacing]: reduceSpacing,
            })}
        >
            <PdpRespSidebarBlurbShipping
                currency={currency}
                item={item}
                viewer={viewer}
                user={user}
                renderTooltip
                sku={relayVariables.isMultiSku ? sku : null}
                isClient={relayVariables.isClient}
                itemQuantity={itemQuantity}
                shortLinkCta
            />

            {isExposedReturnsClientVariant && (
                <ClientSuspense fallback={null}>
                    <PdpSharedBlurb
                        zeroPadding
                        IconComponent={ReturnArrow}
                        iconClass={styles.returnsIcon}
                    >
                        <PdpSharedSidebarItemReturns item={item} user={user} />
                    </PdpSharedBlurb>
                </ClientSuspense>
            )}

            <PdpSharedBlurb zeroPadding IconComponent={CheckCircle} iconClass={styles.icon}>
                <PdpSharedSidebarBuyerIncentives
                    user={user}
                    onDetailsClick={buyerIncentivesSidebarClick}
                />
            </PdpSharedBlurb>

            <div className={styles.returnPolicyContainer}>
                <PdpRespSidebarBlurbReturnPolicy item={item} />
            </div>
        </div>
    );
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarBlurbs_viewer on Viewer {
            ...PdpRespSidebarBlurbShipping_viewer
            ...useExposedReturnsClientVariant_viewer @include(if: $isClient)
        }
    `,
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            isAuctionEnabled
            ...useRenderBlurbs_item
            ...PdpRespSidebarBlurbShipping_item
            ...PdpRespSidebarBlurbReturnPolicy_item
            ...PdpSharedSidebarItemReturns_item @include(if: $isClient)
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            serviceId
            ...PdpSharedSidebarBuyerIncentives_user
            ...PdpRespSidebarBlurbShipping_user
            ...PdpRespSidebarBlurbShipping_user
            ...PdpSharedSidebarItemReturns_user @include(if: $isClient)
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            ...useRenderBlurbs_displayInfo
        }
    `,
    sku: graphql`
        fragment PdpRespSidebarBlurbs_sku on ItemSkuType {
            ... on ItemSkuType @include(if: $isMultiSku) {
                serviceId
                ...PdpRespSidebarBlurbShipping_sku
            }
        }
    `,
});
